/**
 * AppOrchestrator - Application initialization
 *
 * Single entry point for bootstrapping the app:
 * - Wires handler callbacks
 * - Loads persisted state (UX settings, IndexedDB data)
 * - Initializes subsystems (EventRouter, UrlStateSync, DialogCoordinator)
 * - Restores URL state
 * - Sets up initial schema, columns, and pagination
 */

import { AppStore } from '../stores/AppStore';
import { SchemaEngine } from '../../core/schema-engine';
import { loadUXSettings } from '../infrastructure/ux-settings';
import { loadInitialData, ensureSourceData, ensureModelData } from '../infrastructure/storage';
import { initEventRouter, destroyEventRouter } from './EventRouter';
import {
  initUrlStateSync,
  destroyUrlStateSync,
  restoreStateFromUrl,
  syncCurrentStateToUrl,
} from './UrlStateSync';
import { setDialogCallbacks } from './DialogCoordinator';

// Handler callback setup functions
import {
  setDialogHandlerCallbacks,
  openDialog,
  closeDialog,
} from '../handlers/dialog/dialog-handlers';
import { setStepCallbacks, computeModelUpToStep } from '../handlers/core/step-handlers';
import { setEdaCallbacks } from '../handlers/core/eda-handlers';
import { setJsonEditCallbacks } from '../handlers/import/json-handlers';
import {
  setImportCallbacks,
  confirmImport,
  confirmTextEntry,
  fetchAndImportFromUrl,
} from '../handlers/import/import-handlers';
import { setGenerateCallbacks, generateData } from '../handlers/import/generate-handlers';

// Direct handler imports for callback wiring
import { confirm, showWarning } from '../handlers/core/notification-handlers';
import { clearColumnSelection, updateToolbarPosition } from '../handlers/core/interaction-handlers';
import { updatePagination } from '../handlers/core/pagination-handlers';

// AppController for orchestration methods only
import { AppController } from './AppController';
import { metricsCollector } from '../infrastructure/metrics';
import i18n from '../../i18n';

let initialized = false;

/**
 * Initialize the application
 */
export async function initApp(): Promise<void> {
  if (initialized) {
    console.warn('App already initialized');
    return;
  }

  const appInitStart = performance.now();

  // Phase 1: Wire handler callbacks
  wireHandlerCallbacks();

  // Phase 2: Load persisted state
  const uxSettings = loadUXSettings();
  AppStore.uxSettings.value = uxSettings;
  AppStore.pageSize.value = uxSettings.pagination.pageSize;
  AppStore.theme.value = uxSettings.theme;
  AppController.applyTheme();

  // Note: i18n language is already initialized from localStorage in src/i18n/index.ts

  const { sources, models, validationWarnings } = await loadInitialData();
  AppStore.sources.value = sources;
  AppStore.models.value = models;

  // Show warning toast if any stored workflows have validation issues
  if (validationWarnings.length > 0) {
    // Delay to ensure ToastContainer is mounted
    setTimeout(() => {
      showWarning(
        i18n.t('notifications.workflowValidationTitle', { ns: 'common' }),
        validationWarnings.join('\n'),
        { duration: 0 }
      );
    }, 500);
  }

  // Phase 3: Initialize subsystems
  initEventRouter();

  initUrlStateSync({
    openDialog: (name, section) => openDialog(name, section),
    switchToModel: (model) => AppController.switchToModel(model),
    switchToSource: (source) => AppController.switchToSource(source),
    showModelInfo: () => AppController.showModelInfo(),
    showDatasetInfo: (source) => AppController.showDatasetInfo(source),
    clearColumnSelection: () => clearColumnSelection(),
  });

  setDialogCallbacks({
    confirm: (msg, label) => confirm(msg, undefined, label),
    clearColumnSelection: () => clearColumnSelection(),
  });

  // Phase 4: Restore URL state (sets activeModel/activeSource but data is still null)
  restoreStateFromUrl(sources, models, {
    openDialog: (name, section) => openDialog(name, section),
    switchToModel: (model) => AppController.switchToModel(model),
    switchToSource: (source) => AppController.switchToSource(source),
    showModelInfo: () => AppController.showModelInfo(),
    showDatasetInfo: (source) => AppController.showDatasetInfo(source),
    clearColumnSelection: () => clearColumnSelection(),
  });

  // Phase 5: Lazy-load data for the active source/model only
  await loadActiveData();

  // Phase 6: Initialize data state
  const activeModel = AppStore.activeModel.value;
  if (activeModel) {
    AppStore.activeStepIndex.value =
      activeModel.steps?.length > 0 ? activeModel.steps.length - 1 : null;
    AppStore.viewingIntermediate.value = false;
  }

  initializeColumnsAndSchema();

  updatePagination();

  // Sync URL state after initial render
  setTimeout(() => syncCurrentStateToUrl(), 0);

  metricsCollector.record({
    transformType: 'app:init',
    durationMs: performance.now() - appInitStart,
    success: true,
  });

  initialized = true;
}

/**
 * Wire all handler callback registrations.
 * Connects handler modules to each other via callback interfaces.
 */
function wireHandlerCallbacks(): void {
  setDialogHandlerCallbacks({
    confirm: (msg, label) => confirm(msg, undefined, label),
    clearColumnSelection: () => clearColumnSelection(),
    openDialog: (dialog, section) => openDialog(dialog, section),
    switchToModel: (model) => AppController.switchToModel(model),
    switchToSource: (source) => AppController.switchToSource(source),
    showModelInfo: () => AppController.showModelInfo(),
    showDatasetInfo: (source) => AppController.showDatasetInfo(source),
  });

  setStepCallbacks({
    updatePagination: () => updatePagination(),
    openDialog: (name, section) => openDialog(name, section),
    closeDialog: (force) => closeDialog(force),
    confirmImport: () => confirmImport(),
    confirmTextEntry: () => confirmTextEntry(),
    fetchAndImportFromUrl: () => fetchAndImportFromUrl(),
    generateData: () => generateData(),
  });

  setEdaCallbacks({
    updateToolbarPosition: () => updateToolbarPosition(),
    clearColumnSelection: () => clearColumnSelection(),
  });

  setJsonEditCallbacks({
    computeModelUpToStep: (model, stepIndex) => computeModelUpToStep(model, stepIndex),
    updatePagination: () => updatePagination(),
  });

  setImportCallbacks({
    openDialog: (name, section) => openDialog(name, section),
    closeDialog: (force) => closeDialog(force),
    createSource: (file, name, columns, data, headerMode, delimiter, customHeaders, format) =>
      AppController.createSource(
        file,
        name,
        columns,
        data,
        headerMode,
        delimiter,
        customHeaders,
        format
      ),
  });

  setGenerateCallbacks({
    updatePagination: () => updatePagination(),
    closeDialog: (force) => closeDialog(force),
  });
}

/**
 * Load data for whatever is currently active (source or model).
 * Called once during init after URL state is restored.
 */
async function loadActiveData(): Promise<void> {
  const activeModel = AppStore.activeModel.value;
  const activeSource = AppStore.activeSource.value;

  if (activeModel) {
    await ensureModelData(activeModel);
    AppStore.currentData.value = activeModel.data;
    // Also ensure the upstream source data is available (needed for recomputation)
    const source = AppStore.sources.value.find((s) => s.id === activeModel.sourceId);
    if (source) {
      await ensureSourceData(source);
    }
  } else if (activeSource) {
    await ensureSourceData(activeSource);
    AppStore.currentData.value = activeSource.data;
  }
}

/**
 * Initialize columns and schema from current data
 */
function initializeColumnsAndSchema(): void {
  const currentData = AppStore.currentData.value;
  const activeModel = AppStore.activeModel.value;
  const activeSource = AppStore.activeSource.value;

  if (currentData && currentData.length > 0) {
    if (activeModel && (!activeModel.schema || activeModel.schema.length === 0)) {
      activeModel.schema = SchemaEngine.createLogicalSchema(activeModel.data!);
    }

    if (activeModel?.schema) {
      AppStore.columns.value = activeModel.schema.map((c: any) => c.name);
    } else if (activeSource?.columns) {
      AppStore.columns.value = activeSource.columns.map((c: any) => c.name);
    } else {
      AppStore.columns.value = Object.keys(currentData[0]);
    }
  }
}

/**
 * Destroy the app and clean up
 */
export function destroyApp(): void {
  destroyEventRouter();
  destroyUrlStateSync();
  initialized = false;
}
